
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useFetchData } from './apiHooks';
import { UserSettingsContext } from 'contexts/UserSettingsContext';
import debounce from 'lodash/debounce';
import { API_ENDPOINTS } from 'constants';
import { useNavigate } from 'react-router-dom';
 
export function useList(type, filter=null, initTab, syncOnInit, initFilters, initPageSize) {
    const navigate = useNavigate();
    const debouncedLineChanges = useRef({});
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    console.log(API_ENDPOINTS, API_ENDPOINTS?.[type.toUpperCase()], type.toUpperCase())
    const { LIST: listUrl, SYNC: syncUrl } = API_ENDPOINTS?.[type.toUpperCase()] || {};
    const { userSettings, updateSetting } = useContext(UserSettingsContext);
    const [selectedAccountSettings, setSelectedAccountSettings] = useState(userSettings?.default_account_settings);
    const { data:list, setData:setList, setData, saveData, isLoading, error, saveError, setSaveError, refetch, isUpdating, queryParams, setQueryParams, setAndSave, 
        triggerUpdate: sync } =  useFetchData(listUrl, syncUrl, false, null, true, false);

    const debouncedRefetch = useMemo(() => 
        debounce((params) => {
            refetch(params);
        }, 300), [refetch]);

    const isMounted = useRef(false);

    useEffect(() => {
        if (syncOnInit && !list && !isMounted.current) {
            if (syncUrl) sync();
            isMounted.current = true;
        }
    }, [syncOnInit, list, syncUrl]);

    const inactiveTags = useMemo(() => {
        if (!list?.tags) return { default: [], red: [], blue: [] };
    
        return {
            default: list.tags.filter(tag => tag.color === 'default').flatMap(tag => tag.tags),
            red: list.tags.filter(tag => tag.color === 'red').flatMap(tag => tag.tags),
            blue: list.tags.filter(tag => tag.color === 'blue').flatMap(tag => tag.tags)
        };
    }, [list?.tags]);    

    useEffect(() => {
        if(queryParams){
            console.log(queryParams)
            debouncedRefetch(queryParams);
        }
    }, [queryParams]); 

    useEffect(() => {
        if(type){
            setList(null)
            setSelectedItems([])
            const lastTab = localStorage.getItem(`${type}_filter_tabs`)
            const storedFilters = userSettings?.list_filters ? JSON.parse(userSettings.list_filters) : {};
            const filters = initFilters ? initFilters : (storedFilters?.[type] ? JSON.stringify(storedFilters?.[type]) : "{}")
            setQueryParams({page: 1, per_page: userSettings?.list_page_length || 10, searchTerm: "", 
                activeTags: [], activeTagsRed: [], activeTagsBlue: [], category: initTab || lastTab || 'all', filter: filter, sort_by: "", sort_order: "", filters: filters})
        }
    }, [type]); 

    const setPerPage = (newPerPage) => {
        updateSetting('list_page_length', newPerPage)
        setQueryParams({...queryParams, page: 1, per_page: newPerPage})
    }

    const handleSort = (columnName) => {
        setQueryParams({...queryParams, 
            sort_by: queryParams.sort_by === columnName && queryParams.sort_order === 'desc' ? "" : columnName, 
            sort_order: queryParams.sort_by === columnName && queryParams.sort_order === 'desc' ? "" : queryParams.sort_order === "asc" ? "desc" : "asc"})
    };

    const toggleTagActive = (clickedTag, type) => {
        const tagType = type === 'default' ? 'activeTags' : type === 'red' ? 'activeTagsRed' : 'activeTagsBlue';

        setQueryParams(prevParams => ({
            ...prevParams,
            [tagType]: prevParams[tagType]?.includes(clickedTag)
                ? prevParams[tagType].filter(tag => tag !== clickedTag)  // Remove tag
                : [...(prevParams[tagType] || []), clickedTag] // Add tag safely
        }));
    };

    const handleSearch = (searchTerm) => {
        setQueryParams({...queryParams, searchTerm})
    }

    const handleFilters = (newFilters, store=false) => {
        console.log(newFilters)
        const currentFilters = queryParams?.filters ? JSON.parse(queryParams.filters) : {};
        console.log(currentFilters)
        const updatedFilters = { ...currentFilters, ...newFilters };
        console.log(updatedFilters)
        
        setQueryParams(prev => ({ ...prev, filters:  JSON.stringify(updatedFilters) }));
        console.log(updatedFilters)
        if(store) {
            const storedFilters = userSettings?.list_filters ? JSON.parse(userSettings.list_filters) : {};
            storedFilters[type] = updatedFilters
            const updatedFiltersString = JSON.stringify(storedFilters);
            updateSetting('list_filters', updatedFiltersString);
        }
    };

    const handleTabClick = (tabId, filterName) => {
        console.log(filterName, tabId)
        if(filterName)
            handleFilters({[filterName]: tabId})
        else
            setQueryParams({...queryParams, page: 1, activeTags: [], activeTagsRed: [], activeTagsBlue: [], category: tabId})
        setSelectedItems([])
    };

    const selectItem = (itemId) => {
        setSelectedItemIndex(itemId);
    }

    const toggleDelete = (id) => {
        toggleLineKey(id, 'is_deleted')
    }

    const debounceSaveLineData = debounce(() => {
        console.log(debouncedLineChanges)
        if (Object.keys(debouncedLineChanges).length > 0) {
            saveData({
                ...list,
                items: Object.keys(debouncedLineChanges.current).map((id) => ({
                    ...debouncedLineChanges.current[id],
                    id
                })),
            });

            debouncedLineChanges.current = {};
        }
    }, 300);

    const toggleMultipleLineKeys = async (key) => {
        selectedItems.forEach(id => {
            toggleLineKey(id, key)
        })
    };

    const setAndSaveMultipleLineKeys = async (key, value) => {
        selectedItems.forEach(id => {
            setAndSaveLineKey(id, key, value)
        })
    };

    const setLineKey = (id, key, value) => {
        if( key === 'is_deleted') {
            setList((prevList) => ({ ...prevList, items: prevList.items.filter(item => item.id !== id) || []}))
        } else {
            setList((prevList) => ({
                ...prevList,
                items: prevList.items.map(line =>
                    line.id === id ? { ...line, [key]: value } : line
                )
            }));
        }
    }
    
    const setAndSaveLineKey = async (id, key, value) => {
        console.log(id, key, value)
        const changedKeys = (debouncedLineChanges.current[id]?.changed_keys || []).slice();
        if (!changedKeys.includes(key)) {
            changedKeys.push(key);
        }
        const changedData = {
            [key]: value,
            changed_keys: changedKeys
        };

        debouncedLineChanges.current[id] = { ...debouncedLineChanges.current[id], ...changedData };
        console.log(debouncedLineChanges)
        debounceSaveLineData();

        if( key === 'is_deleted') {
            setList((prevList) => ({ ...prevList, items: prevList.items.filter(item => item.id !== id) || []}))
        } else {
            setList((prevList) => ({
                ...prevList,
                items: prevList.items.map(line =>
                    line.id === id ? { ...line, [key]: value } : line
                )
            }));
        }
    }

    const toggleLineKey = async (id, key, value=null) => {
        const newValue = value === null ? !list?.items.find(line => line.id === id)?.[key] : value;
        setAndSaveLineKey(id, key, newValue)
    }

    const addLine = async (newLine) => {
        if(!list){
            setSaveError({message: "No List available to add items"})
            return null
        }
        const savedLine = await saveData({list, items: [newLine]})
        if(savedLine) {
            const newData = {list, items: [savedLine[0], ...list.items]}
            setData(newData)
        } else {
            console.error(savedLine)
        }
        return savedLine
    }

    const addLineAndOpen = async (newLine) => {
        const newLines = await addLine(newLine)
        navigate(`/admin/${type}/${newLines[0].id}`, { replace: false });
    }


    const updateLine = (updatedLine) => {
        if(!list){
            setSaveError({message: "No List available to update items"})
            return
        }
        const index = list.items.findIndex(item => item.id === updatedLine.id);
        if (index >= 0) {
            const updatedItems = [
                ...list.items.slice(0, index),
                updatedLine,
                ...list.items.slice(index + 1),
            ];
            const newData = { list, items: updatedItems };
            setList(newData); // Update state with new list
        } else {
            setSaveError({message: "Item not found in the list for update"})
        }
    }

    const handleSetCurrentPage = (newPage) => {
        setQueryParams({...queryParams, page: newPage})
    }

    const handleSetSelectedAccountSettings = (newAccountSettings) => {
        console.log('new email setting', newAccountSettings)
        setSelectedAccountSettings(newAccountSettings)
        updateSetting('default_account_settings', newAccountSettings)

        setQueryParams({...queryParams, account_settings_id: newAccountSettings})
    }

    const toggleSelectedItem = (id, shiftPressed) => {
        const rowIds = list.items.map(row => row.id);
        const newIndex = rowIds.indexOf(id);
    
        if (!selectedItems.length || !shiftPressed || selectedItems.includes(id)) {
            setSelectedItems(prev =>
                prev.includes(id)
                    ? prev.filter(item => item !== id)
                    : [...prev, id]
            );
        } else {
            // Find last selected index in the rows
            let lastSelectedIndex = -1;
            for (let i = list.items.length - 1; i >= 0; i--) {
                if (selectedItems.includes(list.items[i].id)) {
                    lastSelectedIndex = i;
                    break;
                }
            }
    
            if (lastSelectedIndex === -1 || newIndex === -1) return;
    
            const start = Math.min(lastSelectedIndex, newIndex);
            const end = Math.max(lastSelectedIndex, newIndex);
    
            const rangeIds = rowIds.slice(start, end + 1);
    
            // Merge and deduplicate
            const newSelected = Array.from(new Set([...selectedItems, ...rangeIds]));
            setSelectedItems(newSelected);
        }
    };
    
    return {
        list,
        setList,
        saveData,
        addLine,
        updateLine,
        setAndSave,
        setLineKey,
        tags: list?.tags, 
        activeTags: {default: queryParams?.activeTags, red: queryParams?.activeTagsRed, blue: queryParams?.activeTagsBlue},
        inactiveTags: inactiveTags,
        handleFilters,
        toggleTagActive,
        toggleDelete,
        setQueryParams,
        searchTerm: queryParams?.searchTerm, 
        filters: queryParams?.filters && JSON.parse(queryParams.filters) || {},
        handleSearch,
        activeTab: queryParams?.category,
        handleTabClick,
        toggleLineKey,
        currentPage: queryParams?.page, 
        setCurrentPage: handleSetCurrentPage,
        selectedItemIndex, 
        selectItem,
        type, 
        isLoading: !list || isLoading, 
        error, 
        saveError,
        sync,
        isUpdating,
        refetch: () => refetch(queryParams), 
        sort_by: queryParams?.sort_by,
        sort_order: queryParams?.sort_order,
        handleSort,
        perPage: userSettings?.list_page_length, 
        setPerPage,
        selectedAccountSettings, 
        handleSetSelectedAccountSettings,
        setAndSaveLineKey,
        setAndSaveMultipleLineKeys,
        toggleMultipleLineKeys,
        selectedItems, 
        setSelectedItems,
        toggleSelectedItem,
        addLineAndOpen
    };
};
